import { useContext } from 'react';

import { Box, Typography, useTheme } from '@mui/material';

import { LEAGUES } from '@/constants/leagues';
import { PLAYER_STATUS } from '@/constants/player-constants';
import { REDUCERS } from '@/constants/reducers';
import { Context } from '@/context';
import { getMasterPhotoById, getTeamImageById } from '@/helpers/images-helper';
import { getCompetitionImage } from '@/helpers/leagues-helper';
import { getPlayerIconWhenIsNotOk } from '@/helpers/players-helper';
import URL_FACTORY from '@/helpers/url-helper';

import CustomImageComponent from './custom/custom-image';
import CustomLink from './custom/custom-link';
import ApercibidosStatusIcon from './icons/apercibidos-status-icon';
import ParteMedicoIcon from './icons/parte-medico-icon';
import PositionBox from './position-box';

const PlayerGeneralStatusCard = ({ noticia, classes, displayConvocatoria, displayPositionForTeam }) => {
    const { dispatch } = useContext(Context);
    const player = noticia?.player ?? noticia.information;
    const id = noticia?.player?.id ?? noticia?.information?.id ?? noticia?.id;

    const theme = useTheme();

    const handleClick = () => {
        dispatch({
            type: REDUCERS.SHOW_MASTER_ACCIONES_PLAYER_ID,
            payload: id,
        });
    };

    const getConvocatoriaSeleccion = () => {
        if (displayConvocatoria && noticia?.convocatoriaSeleccion?.isConvocado) {
            if (noticia?.convocatoriaSeleccion?.countryCode) {
                return (
                    <Box className="player-information-container__item-convocatoria">
                        <CustomImageComponent
                            src={URL_FACTORY.GetCountryImage(noticia?.convocatoriaSeleccion?.countryCode, 30)}
                            width={20}
                            height={20}
                            alt="selección"
                            style={{ borderRadius: '50%' }}
                        />
                    </Box>
                );
            } else {
                return (
                    <Box className="player-information-container__item-convocatoria">
                        <CustomImageComponent
                            src={getCompetitionImage(LEAGUES.FIFA_FRIENDLIES, 30)}
                            width={20}
                            height={20}
                            alt="selección"
                        />
                    </Box>
                );
            }
        }
    };

    const getTopIcon = () => {
        if (displayPositionForTeam) {
            const width = '16px';
            return (
                <PositionBox
                    positionId={player?.positionId}
                    styles={{ width: width, height: width }}
                    fontSize={'8px'}
                />
            );
        }

        return (
            <CustomImageComponent
                width={20}
                height={20}
                src={getTeamImageById(
                    player?.teamId ?? noticia?.teamId ?? noticia?.team?.id ?? noticia?.team,
                    30
                )}
                alt={'equipo'}
            />
        );
    };

    const getStatusInfo = (p, fontSize = 14) => {
        if (p?.generalStatus?.status === PLAYER_STATUS.OK) {
            return (
                <Box className="player-information-container__item-info-status">
                    <Typography fontSize={fontSize}>
                        {p?.generalStatus?.status === PLAYER_STATUS.OK && 'Disponible para competir'}
                    </Typography>
                </Box>
            );
        }

        if (p?.generalStatus?.statusInfo) {
            let statusInfo = p.generalStatus.statusInfo;

            if (statusInfo.length > 2) {
                statusInfo = statusInfo.charAt(0).toUpperCase() + statusInfo.slice(1);
            }

            const icon = getPlayerIconWhenIsNotOk(
                noticia?.generalStatus?.status,
                noticia?.generalStatus?.statusLevel,
                12,
                12
            );

            return (
                <Box display={'flex'} alignItems={'center'} gap={0.5}>
                    {icon} <Typography fontSize={fontSize}>{statusInfo}</Typography>
                </Box>
            );
        }
    };

    const getReport = (p, fontSize = 14) => {
        if (p?.generalStatus?.report) {
            let report = p.generalStatus.report;

            if (report.length > 2) {
                report = report.charAt(0).toUpperCase() + report.slice(1);
            }

            return (
                <Box className="player-information-container__item-info-status">
                    <Typography fontSize={fontSize}>{report}</Typography>
                </Box>
            );
        }
    };

    return (
        <Box
            className={`player-information-container__item ${classes}`}
            style={{ cursor: 'pointer', borderBottom: `1px solid ${theme.palette.border.main}` }}
        >
            <Box onClick={handleClick} sx={{ mr: 0.5 }}>
                <Box sx={{ position: 'absolute', top: 10, left: 0 }}>{getTopIcon()}</Box>
                <CustomImageComponent
                    width={70}
                    height={70}
                    src={getMasterPhotoById(player.id, 105)}
                    alt={'jugador'}
                />
            </Box>
            <Box className="player-information-container__item-info" onClick={handleClick}>
                <Box display={'flex'} alignItems={'center'} gap={0.5}>
                    <Typography fontSize={14} fontWeight={500}>
                        {player.name ?? player?.information?.name ?? player?.nickname}
                    </Typography>
                    {noticia?.generalStatus?.isApercibido && (
                        <ApercibidosStatusIcon iconWidth={15} iconHeight={15} />
                    )}
                    {getConvocatoriaSeleccion()}
                </Box>

                {getStatusInfo(noticia)}

                {getReport(noticia)}
            </Box>

            {noticia?.generalStatus?.medicalReport && (
                <CustomLink href={noticia?.generalStatus?.medicalReport}>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            position: 'absolute',
                            top: 0,
                            right: '8px',
                        }}
                    >
                        <ParteMedicoIcon width={20} height={20} />
                        <Typography fontSize={12} color="primary.main">
                            Parte médico
                        </Typography>
                    </Box>
                </CustomLink>
            )}
        </Box>
    );
};

export default PlayerGeneralStatusCard;
